import type { FulfillmentRow } from "./fulfillmentSummary";

// Quantities are tonnes with up to 3 decimals; compare with a half-kilogram
// tolerance so float noise from summing lines never reads as an overshoot.
const EPS = 0.0005;

const roundToHundredths = (quantity: number) => Math.round(quantity * 100) / 100;

type BadgeTone = "info" | "success" | "warning" | "critical" | "attention";

/** What the fulfillment summary shows for one order line, and which buttons it offers. */
export function summaryRowStatus(item: FulfillmentRow) {
  const canReserve = item.ordered > item.reserved + item.shipped;
  const canShip = item.reserved > item.pending;
  const remaining = roundToHundredths(item.ordered - item.shipped);
  // A truck can load past what the order or reservation covers (terminalas
  // warns, never blocks) — flag it here rather than showing it as simply
  // "complete".
  const overOrdered = item.shipped - item.ordered > EPS;
  const overReserved =
    item.reservedQuantity > 0 &&
    item.shippedReserved - item.reservedQuantity > EPS;

  let shippedTone: BadgeTone = "warning";
  if (overOrdered) shippedTone = "critical";
  else if (!overReserved && remaining <= 0) shippedTone = "success";

  let overshootTextKey: string | undefined;
  if (overOrdered) overshootTextKey = "deliveries_terminalas_warning_over_ordered";
  else if (overReserved) overshootTextKey = "deliveries_terminalas_warning_over_reserved";

  return {
    // A group line has no single product to pre-select in the dialogs.
    canReserve: !!item.productId && canReserve,
    canShip: !!item.productId && canShip,
    reservedText: String(roundToHundredths(item.reserved)),
    reservedTone: (item.reserved + item.shipped >= item.ordered
      ? "success"
      : "attention") as BadgeTone,
    // Nothing waiting to ship is shown as no badge at all.
    pendingText: item.pending > 0 ? String(roundToHundredths(item.pending)) : null,
    shippedText: String(roundToHundredths(item.shipped)),
    shippedTone,
    overshootTextKey,
    isComplete:
      !canReserve &&
      !canShip &&
      remaining <= 0 &&
      (item.ordered > 0 || item.shipped > 0) &&
      !overOrdered &&
      !overReserved,
  };
}
